import Container = Phaser.GameObjects.Container;
import Graphics = Phaser.GameObjects.Graphics;
import Text = Phaser.GameObjects.Text;

const MAX_TEXT_WIDTH = 128;
const MAX_LINES = 2;
const PADDING_X = 6;
const PADDING_Y = 5;
const RADIUS = 6;
const FILL_COLOR = 0xffffff;
const LINE_COLOR = 0x14304c;
const TEXT_COLOR = "#14304C";

/**
 * A thought bubble showing what an NPC is currently doing.
 *
 * The bubble is redrawn to fit its text every time the text changes: long actions wrap onto
 * at most two lines and are cut with an ellipsis after that, so nothing spills outside the
 * outline. The fill is opaque so that two overlapping bubbles stay readable; which one draws
 * on top is decided by the owning character's depth (characters lower on screen draw on top).
 *
 * Coordinates are relative to the character container; `bottomY` is where the bubble's lower
 * edge sits (just above the name label), with the trailing "thought" circles below it.
 */
export class ThoughtBubble extends Container {
    private readonly graphics: Graphics;
    private readonly text: Text;
    private readonly bottomY: number;

    constructor(scene: Phaser.Scene, bottomY: number) {
        super(scene, 0, 0);
        this.bottomY = bottomY;
        this.graphics = scene.add.graphics();
        this.text = new Text(scene, 0, 0, "", {
            fontFamily: '"Press Start 2P"',
            fontSize: "7px",
            color: TEXT_COLOR,
            align: "center",
            wordWrap: { width: MAX_TEXT_WIDTH, useAdvancedWrap: true },
            // Fixed metrics skip a costly measurement per character; these match the 7px pixel font.
            metrics: {
                ascent: 8,
                descent: 2,
                fontSize: 10,
            },
        });
        this.text.setLineSpacing(2);
        this.text.setOrigin(0.5, 1);
        this.add([this.graphics, this.text]);
        this.setVisible(false);
    }

    public setText(action: string): void {
        const cleaned = (action ?? "").trim();
        if (cleaned === "" || cleaned.toLowerCase() === "idle") {
            this.setVisible(false);
            return;
        }
        this.text.setText(this.clampToLines(cleaned));
        this.redraw();
        this.setVisible(true);
    }

    /** Keep at most MAX_LINES wrapped lines, ending the last kept line with an ellipsis if cut. */
    private clampToLines(action: string): string {
        this.text.setText(action);
        const lines = this.text.getWrappedText(action);
        if (lines.length <= MAX_LINES) {
            return action;
        }
        let last = lines[MAX_LINES - 1].trimEnd();
        const fits = (candidate: string) => {
            this.text.setText(candidate);
            return this.text.width <= MAX_TEXT_WIDTH;
        };
        while (last.length > 0 && !fits(last + "…")) {
            last = last.slice(0, -1).trimEnd();
        }
        return [...lines.slice(0, MAX_LINES - 1), last + "…"].join("\n");
    }

    private redraw(): void {
        const width = Math.ceil(this.text.width) + PADDING_X * 2;
        const height = Math.ceil(this.text.height) + PADDING_Y * 2;
        const left = -width / 2;
        const top = this.bottomY - height;

        this.text.setPosition(0, this.bottomY - PADDING_Y);

        const g = this.graphics;
        g.clear();
        g.fillStyle(FILL_COLOR, 1);
        g.lineStyle(1.5, LINE_COLOR, 1);
        g.fillRoundedRect(left, top, width, height, RADIUS);
        g.strokeRoundedRect(left, top, width, height, RADIUS);
        // Trailing circles leading from the bubble down toward the head, kept clear of the name label.
        g.fillCircle(7, this.bottomY + 3, 2.5);
        g.strokeCircle(7, this.bottomY + 3, 2.5);
        g.fillCircle(11, this.bottomY + 8, 1.5);
        g.strokeCircle(11, this.bottomY + 8, 1.5);
    }
}
